import type { AtomContext, RenderLocals } from "./registry-types";
import { invokeTrigger } from "./render-ui-node.utils";

function isTextControlValue(value: unknown): value is string | number {
  return typeof value === "string" || typeof value === "number";
}

function resolveTextControlValue(
  props: Record<string, unknown>,
  currentValue: unknown,
): string {
  const nextValue =
    isTextControlValue(currentValue)
      ? currentValue
      : isTextControlValue(props.value)
        ? props.value
        : isTextControlValue(props.defaultValue)
          ? props.defaultValue
          : "";
  return String(nextValue);
}

function resolveBooleanControlValue(
  props: Record<string, unknown>,
  currentValue: unknown,
): boolean {
  const nextValue =
    currentValue !== undefined && currentValue !== null
      ? currentValue
      : Object.prototype.hasOwnProperty.call(props, "checked")
        ? props.checked
        : Object.prototype.hasOwnProperty.call(props, "defaultChecked")
          ? props.defaultChecked
          : false;
  return Boolean(nextValue);
}

export function extractFormControlValue(args: readonly unknown[]): unknown {
  const first = args[0];
  if (typeof first === "boolean") return first;

  if (first && typeof first === "object") {
    const event = first as {
      target?: unknown;
      currentTarget?: unknown;
      checked?: unknown;
      value?: unknown;
    };

    const target = (event.currentTarget ?? event.target) as
      | {
          type?: string;
          checked?: boolean;
          value?: unknown;
        }
      | undefined;

    if (target) {
      const targetType =
        typeof target.type === "string" ? target.type.toLowerCase() : "";

      if (targetType === "checkbox") {
        if (typeof target.checked === "boolean") return target.checked;
        if (target.value !== undefined) return target.value;
      }

      if (targetType === "radio") {
        if (target.value !== undefined) return target.value;
        if (typeof event.value === "string" || typeof event.value === "number") {
          return event.value;
        }
        if (typeof target.checked === "boolean") return target.checked;
      } else {
        if (target.value !== undefined) return target.value;
        if (typeof event.value === "string" || typeof event.value === "number") {
          return event.value;
        }
        if (typeof event.checked === "boolean") return event.checked;
      }

      if (typeof target.checked === "boolean") return target.checked;
    }

    if (typeof event.checked === "boolean") return event.checked;
    if (event.value !== undefined) return event.value;
  }

  return first;
}

function resolveChoiceValue(props: Record<string, unknown>): string | number | undefined {
  const explicitValue = props.value;
  if (typeof explicitValue === "string" || typeof explicitValue === "number") {
    return explicitValue;
  }

  const label = props.label;
  if (typeof label === "string" || typeof label === "number") {
    return label;
  }

  return undefined;
}

function isChoiceSelected(
  currentValue: unknown,
  choiceValue: string | number,
): boolean {
  if (Array.isArray(currentValue)) {
    return currentValue.some((item) => String(item) === String(choiceValue));
  }

  if (currentValue === undefined || currentValue === null) {
    return false;
  }

  return String(currentValue) === String(choiceValue);
}

function toggleChoiceSelection(
  currentValue: unknown,
  choiceValue: string | number,
): unknown {
  const currentItems = Array.isArray(currentValue)
    ? currentValue
    : currentValue === undefined || currentValue === null
      ? []
      : [currentValue];

  const choiceKey = String(choiceValue);
  const isActive = currentItems.some((item) => String(item) === choiceKey);

  return isActive
    ? currentItems.filter((item) => String(item) !== choiceKey)
    : [...currentItems, choiceValue];
}

export interface TextControlOptions {
  triggerName?: string | undefined;
  ctx: AtomContext;
  locals: RenderLocals;
  eventPayload: unknown;
  /**
   * Whether the underlying control also emits a Radix-style
   * `onValueChange(value)` callback (the Select atom does, like Chip).
   * Without this, a selection is never reported through `onChange`, so
   * `setFormValue` never runs and submitted form values stay empty —
   * which silently broke flows that read `@form.<name>` after a Select.
   */
  emitValueChange?: boolean;
}

export interface BooleanControlOptions {
  triggerName?: string | undefined;
  ctx: AtomContext;
  locals: RenderLocals;
  eventPayload: unknown;
  /**
   * Whether the underlying component understands the Radix-style
   * `onCheckedChange` handler. Styled Switch/Checkbox atoms do; nodes that
   * render a native `<input>` do not, and React warns about the unknown
   * property there, so callers suppress it for those shapes.
   */
  allowOnCheckedChange?: boolean;
}

/**
 * Bind a text-like control (Input/Textarea/Select or Box as="input").
 *
 * When no value exists yet — no bound form value and no authored
 * value/defaultValue — the control is rendered UNCONTROLLED with a
 * `defaultValue`. React hydration does not reset uncontrolled inputs, so
 * characters the user typed before the JS bundle loaded survive hydration.
 * Forcing a controlled `value=""` here used to wipe that pre-hydration text
 * the moment React took over. The control becomes controlled only once a
 * value actually exists (user typed, `form:set`, or an authored binding).
 */
export function bindTextControl(
  props: Record<string, unknown>,
  fieldName: string | undefined,
  currentValue: unknown,
  setFormValue: (field: string, value: unknown) => void,
  options: TextControlOptions,
): void {
  const hasBoundValue =
    (currentValue !== undefined && currentValue !== null) ||
    isTextControlValue(props.value) ||
    isTextControlValue(props.defaultValue);

  if (hasBoundValue) {
    props.value = resolveTextControlValue(props, currentValue);
    delete props.defaultValue;
  } else {
    // No value yet: stay uncontrolled so pre-hydration typing is preserved.
    delete props.value;
    if (props.defaultValue === undefined) props.defaultValue = "";
  }

  const commitTextValue = (...args: unknown[]) => {
    const nextValue = extractFormControlValue(args);
    if (fieldName) {
      setFormValue(fieldName, nextValue);
    }
    if (options.triggerName) {
      invokeTrigger(
        options.ctx,
        options.triggerName,
        options.locals,
        options.eventPayload,
        { value: nextValue, search: nextValue },
      );
    }
  };

  props.onChange = commitTextValue;

  // Radix-style value controls (Select) report selection through
  // `onValueChange` instead of a native change event. Wire the same commit
  // path so the chosen value reaches form state (and any onChangeTrigger).
  if (options.emitValueChange) {
    props.onValueChange = commitTextValue;
  }
}

/**
 * Bind a boolean control (Switch/Checkbox/Radio/Toggle or native checkbox).
 *
 * Same hydration contract as {@link bindTextControl}: when neither a bound
 * form value nor an authored checked/defaultChecked prop exists, the control
 * renders uncontrolled via `defaultChecked` so pre-hydration user toggles
 * survive React taking over. Change handlers are always wired so the first
 * post-hydration interaction is captured into form state.
 */
export function bindBooleanControl(
  props: Record<string, unknown>,
  fieldName: string | undefined,
  currentValue: unknown,
  setFormValue: (field: string, value: unknown) => void,
  options: BooleanControlOptions,
): void {
  const hasBoundChecked =
    (currentValue !== undefined && currentValue !== null) ||
    Object.prototype.hasOwnProperty.call(props, "checked") ||
    Object.prototype.hasOwnProperty.call(props, "defaultChecked");

  if (hasBoundChecked) {
    props.checked = resolveBooleanControlValue(props, currentValue);
    delete props.defaultChecked;
  } else {
    delete props.checked;
    if (!Object.prototype.hasOwnProperty.call(props, "defaultChecked")) {
      props.defaultChecked = false;
    }
  }

  const updateBooleanValue = (next: boolean) => {
    if (fieldName) {
      setFormValue(fieldName, next);
    }

    invokeTrigger(options.ctx, options.triggerName, options.locals, options.eventPayload, {
      checked: next,
    });
  };

  // Styled boolean atoms (Switch, Checkbox, …) consume the Radix-style
  // `onCheckedChange`. Native `<input>` elements do not — React logs an
  // "Unknown event handler property" warning for it there — so the handler
  // is only wired when the caller confirms the component supports it.
  if (options.allowOnCheckedChange !== false) {
    props.onCheckedChange = updateBooleanValue;
  }

  // Boolean controls may render as a native<input type="checkbox"> (React
  // warns "checked without onChange" for that shape). Always provide the
  // standard React `onChange` so the controlled-input contract is satisfied
  // regardless of which element the atom renders.
  props.onChange = (...args: unknown[]) => {
    const next = extractFormControlValue(args);
    if (typeof next === "boolean") {
      updateBooleanValue(next);
    }
  };
}

export function bindChoiceControl(
  props: Record<string, unknown>,
  fieldName: string,
  currentValue: unknown,
  setFormValue: (field: string, value: unknown) => void,
): void {
  const choiceValue = resolveChoiceValue(props);
  if (choiceValue !== undefined) {
    props.value = choiceValue;
    props.selected =
      currentValue !== undefined && currentValue !== null
        ? String(currentValue) === String(choiceValue)
        : false;
  }

  props.onChange = () => {
    if (choiceValue !== undefined) {
      setFormValue(fieldName, choiceValue);
    }
  };

  props.onClick = () => {
    if (choiceValue !== undefined) {
      setFormValue(fieldName, choiceValue);
    }
  };
}

export function bindMultipleChoiceControl(
  props: Record<string, unknown>,
  fieldName: string,
  currentValue: unknown,
  setFormValue: (field: string, value: unknown) => void,
): void {
  const choiceValue = resolveChoiceValue(props);
  if (choiceValue !== undefined) {
    props.value = choiceValue;
    props.selected = isChoiceSelected(currentValue, choiceValue);
  }

  const updateChoiceValue = () => {
    if (choiceValue !== undefined) {
      setFormValue(fieldName, toggleChoiceSelection(currentValue, choiceValue));
    }
  };

  props.onChange = updateChoiceValue;
  props.onClick = updateChoiceValue;
}

export function bindChipGroupControl(
  props: Record<string, unknown>,
  fieldName: string | undefined,
  currentValue: unknown,
  setFormValue: (field: string, value: unknown) => void,
  options: {
    triggerName?: string | undefined;
    ctx: AtomContext;
    locals: RenderLocals;
    eventPayload: unknown;
  },
): void {
  if (fieldName && currentValue !== undefined) {
    props.value = currentValue;
  }

  const updateValue = (nextValue: unknown) => {
    if (fieldName) {
      setFormValue(fieldName, nextValue);
    }

    invokeTrigger(options.ctx, options.triggerName, options.locals, options.eventPayload, {
      value: nextValue,
    });
  };

  props.onValueChange = updateValue;
}